import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { type PaymentHistory } from "@/repositories/interfaces";
import { repositoryFactory } from "@/repositories/RepositoryFactory";

export const usePaymentHistory = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const paymentHistoryRepo = repositoryFactory.getPaymentHistoryRepository();
  const paymentQueryKey = ["paymentHistory", user?.id];
  const subscriptionQueryKey = ["subscriptions", user?.id];

  const {
    data: payments = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: paymentQueryKey,
    queryFn: () => paymentHistoryRepo.getAll(),
    enabled: !!user,
  });

  const createPayment = useMutation({
    mutationKey: ["createPayment"],
    mutationFn: (payment: Omit<PaymentHistory, "id" | "created_at">) =>
      paymentHistoryRepo.create(payment),
    onMutate: async (newItem) => {
      await queryClient.cancelQueries({ queryKey: paymentQueryKey });
      const previous =
        queryClient.getQueryData<PaymentHistory[]>(paymentQueryKey);
      queryClient.setQueryData<PaymentHistory[]>(
        paymentQueryKey,
        (old = []) => [
          {
            ...newItem,
            id: crypto.randomUUID(),
            created_at: new Date().toISOString(),
          } as PaymentHistory,
          ...old,
        ],
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(paymentQueryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: paymentQueryKey });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });
    },
  });

  const updatePayment = useMutation({
    mutationFn: ({
      id,
      ...updates
    }: Partial<PaymentHistory> & { id: string }) =>
      paymentHistoryRepo.update(id, updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentQueryKey });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });
    },
  });

  const deletePayment = useMutation({
    mutationKey: ["deletePayment"],
    mutationFn: (id: string) => paymentHistoryRepo.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: paymentQueryKey });
      const previous =
        queryClient.getQueryData<PaymentHistory[]>(paymentQueryKey);
      queryClient.setQueryData<PaymentHistory[]>(
        paymentQueryKey,
        (old = []) => old.filter((p) => p.id !== id),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(paymentQueryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: paymentQueryKey });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });
    },
  });

  const bulkCreatePayments = useMutation({
    mutationFn: (payments: Omit<PaymentHistory, "id" | "created_at">[]) =>
      paymentHistoryRepo.bulkCreate(payments),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentQueryKey });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });
    },
  });

  const clearAllPayments = useMutation({
    mutationFn: () => paymentHistoryRepo.deleteAll(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentQueryKey });
      void queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });
    },
  });

  const getPaymentsBySubscription = (subscriptionId: string) => {
    return payments.filter(
      (payment) => payment.subscription_id === subscriptionId,
    );
  };

  return {
    payments,
    isLoading,
    error,
    createPayment,
    updatePayment,
    deletePayment,
    bulkCreatePayments,
    clearAllPayments,
    getPaymentsBySubscription,
  };
};
